// ─────────────────────────────────────────────────────────────────────────────
// MackPage — the macro agent's dashboard SHELL: identity header, the nine-tab
// strip, and Mack's own scroll frame. Each tab lives in its own file under
// mack/; this file owns only what is shared across all of them.
//
// SEPARATE BUILDING from Arthur. Mack is file-walled from Arthur at the store
// level (core/harness_topology.py:86 — his dashboard_source is macro_data/
// mack_pulse.json, never memory/trading.json), and that wall is honoured here:
// his page is its own component tree, not a branch inside TradingPage.tsx.
//
// BUILD-04 extracted the tabs out of this file. Shared tokens live in
// mack/tokens.ts, shared surfaces and controls in mack/primitives.tsx. The
// extraction was a pure refactor — rendered output byte-identical before and
// after — so each tab's provenance is now a FILE hash rather than a line range.
//
// TOKEN DUPLICATION WITH ARTHUR REMAINS (deliberate): TradingPage.tsx does not
// export its `C` tokens and Mack's builds are not permitted to touch that file.
// mack/tokens.ts is Mack's own sampled palette; reconciling the two needs a
// build that may edit Arthur's file, which is a separate decision.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState } from 'react';
import { C, MONO, SANS } from './mack/tokens';
import { CalendarTab } from './mack/CalendarTab';
import { InputsDecisionTab as InputsTab } from './mack/InputsDecision'; // decision interface (2026-10-14); evidence view: import { InputsTab } from './mack/InputsTab';
import { CalculatorDecisionTab as CalculatorTab } from './mack/CalculatorDecision'; // decision interface (2026-10-14); evidence view: import { CalculatorTab } from './mack/CalculatorTab';
import { PhasesTab } from './mack/PhasesTab';
import { FlowTab } from './mack/FlowTab';
import { CascadeTab } from './mack/CascadeTab';
import { BacktestTab } from './mack/BacktestTab';
import { ReferenceTab } from './mack/ReferenceTab';
import { OverviewTab } from './mack/OverviewTab';

// ── TAB STRIP ────────────────────────────────────────────────────────────────
// Labels are EXACTLY the nine strings pinned in core/harness_topology.py:86 and
// guarded by tests/test_stage2_step1_harness_v1.py:66. Parity is load-bearing.
//
// `step` is UI DECORATION, never part of the label — exactly what the harness
// comment anticipated ("NO leading step numbers (those are UI decoration, not
// labels)"). The design confirms they exist in the UI: 1..7 on Calendar through
// Backtest, with Overview and Reference unnumbered.
const MACK_TABS = [
  { key: 'overview',   label: 'Overview',           step: null },
  { key: 'calendar',   label: 'Calendar',           step: 1    },
  { key: 'inputs',     label: 'Inputs',             step: 2    },
  { key: 'calculator', label: 'Calculator',         step: 3    },
  { key: 'phases',     label: 'Phases',             step: 4    },
  { key: 'flow',       label: 'Flow & Feasibility', step: 5    },
  { key: 'cascade',    label: 'Cascade',            step: 6    },
  { key: 'backtest',   label: 'Backtest',           step: 7    },
  { key: 'reference',  label: 'Reference',          step: null },
] as const;

type MackTabKey = typeof MACK_TABS[number]['key'];

// The header orb from the design — amber ring, triangle-and-eye mark inside.
function MackMark() {
  return (
    <div style={{
      width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
      border: `1px solid ${C.amberDim}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true"
           fill="none" stroke={C.amber} strokeWidth="1.1" strokeLinejoin="round">
        <path d="M10 3.4 17 16.2H3z" />
        <circle cx="10" cy="11.6" r="1.9" />
        <circle cx="10" cy="11.6" r="0.55" fill={C.amber} stroke="none" />
      </svg>
    </div>
  );
}

// ── PAGE ─────────────────────────────────────────────────────────────────────

export interface MackPageProps {
  // Accepted for parity with TradingPage's mount signature so AgentViewScreen
  // passes the same props. Build-01 makes no API calls — Forge's wiring uses it.
  authToken?: string | null;
  onBack?: () => void;
}

export default function MackPage(props: MackPageProps) {
  // Calendar is the default tab for build-01: Overview sits first in the strip
  // but is the last surface built, so landing on it would open onto an empty frame.
  const [tab, setTab] = useState<MackTabKey>('calendar');

  // The badge reflects persisted Calendar alerts reported by the panel.
  const [calendarAlertCount, setCalendarAlertCount] = useState(0);

  const d = new Date();
  const hobart = (opts: Intl.DateTimeFormatOptions) =>
    d.toLocaleDateString('en-AU', { ...opts, timeZone: 'Australia/Hobart' });
  const dateLine = `${hobart({ weekday: 'long' })} ${hobart({ day: 'numeric' })} ${hobart({ month: 'long' })}`;

  return (
    <div style={{
      // Mack's OWN scroll frame, following the CodePage / TradingPage pattern:
      // height:100% (never 100vh) so the domain bar's height is respected, and
      // the page owns its scroller. AppShell is NOT touched — it clips at 100vh
      // and changing it would move every page in the OS.
      height: '100%',
      minHeight: 0,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      background: C.bg,
      color: C.text,
      fontFamily: SANS,
      padding: 0,
    }}>

      {/* Header — sits on the page ground in the design, not on a card */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 16,
        padding: '16px 24px 0',
        flexShrink: 0,
      }}>
        <div style={{ width: '100%', minWidth: 0, maxWidth: 1480, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 16 }}>
          <MackMark />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: C.text, letterSpacing: '-0.01em', overflowWrap: 'anywhere' }}>
              Mack — Macro Domain
            </div>
            <div style={{ fontSize: 13, color: C.textSub, marginTop: 5 }}>
              Hobart · {dateLine} · <span style={{ color: C.amber }}>acquisition review</span> · downstream calculations disabled
            </div>
          </div>
        </div>
      </div>

      {/* Nine-tab strip — labels pinned to core/harness_topology.py:86; the step
          numerals and the alert badge are decoration layered around them. */}
      <div style={{ padding: '0 24px', flexShrink: 0 }}>
        <div style={{
          width: '100%', maxWidth: 1480, margin: '0 auto',
          display: 'flex', gap: 26, alignItems: 'flex-end',
          borderBottom: `1px solid ${C.rule}`,
          marginTop: 20, overflowX: 'auto',
        }}>
          {MACK_TABS.map(t => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                aria-current={active ? 'page' : undefined}
                style={{
                  display: 'flex', alignItems: 'center', gap: 7,
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontFamily: SANS, fontSize: 15,
                  fontWeight: active ? 700 : 400,
                  color: active ? '#ffffff' : C.textSub,
                  padding: '0 0 11px', whiteSpace: 'nowrap',
                  borderBottom: `2px solid ${active ? C.amber : 'transparent'}`,
                  marginBottom: -1,
                  transition: 'color 0.15s',
                }}
              >
                {t.step !== null && (
                  <span style={{ fontSize: 11, fontFamily: MONO, color: C.amberDim, fontWeight: 400 }}>
                    {t.step}
                  </span>
                )}
                {t.label}
                {t.key === 'calendar' && calendarAlertCount > 0 && (
                  <span style={{
                    minWidth: 18, height: 18, borderRadius: 9, padding: '0 5px',
                    background: C.red, color: '#ffffff',
                    fontSize: 10.5, fontWeight: 700, fontFamily: SANS,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {calendarAlertCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Scroll region — this page's only scroller. Header and tab strip stay put
          above it. Scrollbar chrome is already invisible OS-wide (index.css,
          owner ruling 2026-07-24) — inherited here, never re-declared. */}
      <div style={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch',
      }}>
        <div style={{ padding: '22px 24px 32px', maxWidth: 1480, margin: '0 auto' }}>

          {tab === 'overview' && <OverviewTab />}

          {tab === 'calendar' && <CalendarTab onAlertCount={setCalendarAlertCount} authToken={props.authToken} />}

          {tab === 'inputs' && <InputsTab authToken={props.authToken} onCalendarEvent={() => setTab('calendar')} />}
          {tab === 'calculator' && <CalculatorTab authToken={props.authToken} />}
          {tab === 'phases' && <PhasesTab authToken={props.authToken} />}
          {tab === 'flow' && <FlowTab authToken={props.authToken} />}
          {tab === 'cascade' && <CascadeTab />}
          {tab === 'backtest' && <BacktestTab />}
          {tab === 'reference' && <ReferenceTab />}

        </div>
      </div>
    </div>
  );
}
